import {useState} from "react";
import {InputGroup, Popover, Spinner} from "@blueprintjs/core";
import InfiniteScroll from "react-infinite-scroll-component";
import styled from "@emotion/styled";
import {COLORS, DEFAULT_POPOVER_MODIFIERS} from "../../../constants/constants";
import {DriverPickerButton, DriverPickerName, DriverPickerPlaceholder} from "../styles/styleds";

export type InfiniteDriverOption = {driverId: number; driverLabel: string};

type Props = {
  testId: string;
  /** Selected driverId as a string ("" = nothing picked). */
  value: string;
  /** Label of the current selection — kept even when server-search filters it out of `options`. */
  selectedLabel?: string;
  /** Accumulated, already-excluded options for the current page(s). */
  options: InfiniteDriverOption[];
  loading: boolean;
  hasMore: boolean;
  onSelect: (value: string) => void;
  /** Server-side search (debounced by the caller), same fields as the Step-1 grid. */
  onSearch: (query: string) => void;
  onLoadMore: () => void;
};

/**
 * DP-3039: single-select driver dropdown for the "Other drivers" scope — a search box that
 * queries server-side (like the Step-1 grid) plus infinite scroll over the paginated results.
 */
const DriverInfiniteSelect = ({
  testId,
  value,
  selectedLabel,
  options,
  loading,
  hasMore,
  onSelect,
  onSearch,
  onLoadMore
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Prefer the live option's label; fall back to the captured selectedLabel when a search has
  // filtered the picked driver out of the visible page.
  const displayLabel = value
    ? options.find((option) => String(option.driverId) === value)?.driverLabel ?? selectedLabel
    : undefined;
  const listId = `${testId}-infinite-list`;

  const resetSearch = () => {
    setQuery("");
    onSearch("");
  };

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    onSearch(event.target.value);
  };

  return (
    <Popover
      isOpen={isOpen}
      onInteraction={(nextOpen) => {
        setIsOpen(nextOpen);
        if (!nextOpen) resetSearch();
      }}
      minimal
      matchTargetWidth
      placement="bottom-start"
      modifiers={DEFAULT_POPOVER_MODIFIERS}
      content={
        <Menu>
          <SearchRow>
            <InputGroup
              data-testid={`${testId}-search`}
              leftIcon="search"
              placeholder="Search driver..."
              value={query}
              onChange={handleQueryChange}
            />
          </SearchRow>
          <List id={listId} data-testid={`${testId}-list`}>
            <InfiniteScroll
              dataLength={options.length}
              next={onLoadMore}
              hasMore={hasMore}
              loader={<Loader size={16} />}
              scrollableTarget={listId}
            >
              {options.map((option) => (
                <OptionRow
                  key={option.driverId}
                  data-testid={`${testId}-option`}
                  onClick={() => {
                    onSelect(String(option.driverId));
                    setIsOpen(false);
                    resetSearch();
                  }}
                >
                  {option.driverLabel}
                </OptionRow>
              ))}
            </InfiniteScroll>
            {options.length === 0 && <EmptyRow>{loading ? <Loader size={16} /> : "No drivers found"}</EmptyRow>}
          </List>
        </Menu>
      }
    >
      <DriverPickerButton
        data-testid={testId}
        alignText="left"
        endIcon="caret-down"
        text={
          displayLabel ? (
            <DriverPickerName>{displayLabel}</DriverPickerName>
          ) : (
            <DriverPickerPlaceholder>Select driver...</DriverPickerPlaceholder>
          )
        }
      />
    </Popover>
  );
};

export default DriverInfiniteSelect;

const Menu = styled.div`
  background: ${COLORS.WHITE};
  border-radius: 4px;
  box-shadow: 0px 2px 6px 0px #0000001a;
  width: 100%;
  min-width: 260px;
`;

const SearchRow = styled.div`
  padding: 8px;
  box-shadow: 0px 1px 2px 0px #0000001a;

  .bp5-input {
    height: 32px;
    border-radius: 4px;
    box-shadow: none;
    border: 1px solid #c3c3c3;
  }
`;

const List = styled.div`
  max-height: 220px;
  overflow-y: auto;
  padding: 4px;
`;

const OptionRow = styled.div`
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 14px;
  color: ${COLORS.TEXT_BLACK};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  :hover {
    background: #0038ff1a;
  }
`;

const EmptyRow = styled.div`
  padding: 12px 8px;
  font-size: 13px;
  color: ${COLORS.TEXT_GRAY_MEDIUM};
  text-align: center;
`;

const Loader = styled(Spinner)`
  padding: 8px 0;
`;
